/**
 * Dataset Service — queries the gpkg_server catalog to discover available
 * datasets and layers, and fetches TileJSON metadata for MapLibre sources.
 *
 * Results are memoized with a 5-minute TTL so panels / hooks that call the
 * same endpoints repeatedly don't hammer the backend.
 */
import { ENDPOINTS, buildTileJsonUrl } from '../config/environment';
import { getAuthHeader } from './AuthContext';

export interface DatasetLayer {
  name: string;
  /** Catalog layer type reported by gpkg_server. */
  kind: 'tiles' | 'raster-tiles' | 'vector-tiles' | 'features' | 'attributes';
  /** Raster content-type or MVT if known. */
  contentType?: string;
  minZoom?: number;
  maxZoom?: number;
  bounds?: [number, number, number, number];
  identifier?: string;
  description?: string;
  srs_id?: number;
  bbox?: [number, number, number, number] | null;
  min_zoom?: number | null;
  max_zoom?: number | null;
}

export interface DatasetSummary {
  id: string;
  title?: string;
  description?: string;
  layers: DatasetLayer[] | Record<string, DatasetLayer>;
  /** `gpkg` or `mbtiles`. */
  kind?: string;
  archive?: string;
  file?: string;
}

export interface TileJson {
  tilejson?: string;
  name?: string;
  tiles: string[];
  minzoom?: number;
  maxzoom?: number;
  bounds?: [number, number, number, number];
  format?: string;
  vector_layers?: Array<{ id: string; fields?: Record<string, string>; minzoom?: number; maxzoom?: number }>;
}

export interface DatasetRefreshResult {
  count: number;
  dataset_ids: string[];
}

export interface DatasetUploadResult extends DatasetRefreshResult {
  uploaded: {
    file: string;
    bytes: number;
  };
}

// ─── In-memory cache (TTL) ───────────────────────────────────────────────────

interface CacheEntry<T> { value: T; expiresAt: number; }
const TTL_MS = 5 * 60 * 1000;
const cache = new Map<string, CacheEntry<unknown>>();

function getCached<T>(key: string): T | undefined {
  const e = cache.get(key);
  if (!e) return undefined;
  if (e.expiresAt < Date.now()) { cache.delete(key); return undefined; }
  return e.value as T;
}

function setCached<T>(key: string, value: T): void {
  cache.set(key, { value, expiresAt: Date.now() + TTL_MS });
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: { ...getAuthHeader() } });
  if (!res.ok) throw new Error(`${url} \u2192 ${res.status} ${res.statusText}`);
  return res.json() as Promise<T>;
}

// ─── Public API ──────────────────────────────────────────────────────────────

export async function listDatasets(force = false): Promise<DatasetSummary[]> {
  const key = 'datasets';
  if (!force) {
    const hit = getCached<DatasetSummary[]>(key);
    if (hit) return hit;
  }
  const data = await fetchJson<{ datasets?: DatasetSummary[] } | DatasetSummary[]>(ENDPOINTS.gpkg.datasets);
  const datasets: DatasetSummary[] = Array.isArray(data) ? data : (data.datasets ?? []);
  setCached(key, datasets);
  return datasets;
}

export async function getDataset(id: string, force = false): Promise<DatasetSummary | null> {
  const key = `dataset:${id}`;
  if (!force) {
    const hit = getCached<DatasetSummary>(key);
    if (hit) return hit;
  }
  try {
    const data = await fetchJson<DatasetSummary>(`${ENDPOINTS.gpkg.datasets}/${encodeURIComponent(id)}`);
    setCached(key, data);
    return data;
  } catch {
    return null;
  }
}

export async function getTileJson(dataset: string, layer: string, force = false): Promise<TileJson | null> {
  const key = `tilejson:${dataset}:${layer}`;
  if (!force) {
    const hit = getCached<TileJson>(key);
    if (hit) return hit;
  }
  try {
    const data = await fetchJson<TileJson>(buildTileJsonUrl(dataset, layer));
    setCached(key, data);
    return data;
  } catch {
    return null;
  }
}

/** Normalize catalog layers because gpkg_server returns a keyed object in production. */
export function getDatasetLayers(dataset: DatasetSummary): DatasetLayer[] {
  return Array.isArray(dataset.layers) ? dataset.layers : Object.values(dataset.layers ?? {});
}

export async function refreshDatasetCatalog(): Promise<DatasetRefreshResult> {
  const res = await fetch(ENDPOINTS.gpkg.datasets + '/refresh', {
    method: 'POST',
    headers: { ...getAuthHeader() },
  });
  if (!res.ok) throw new Error(`Catalog refresh failed: ${res.status} ${res.statusText}`);
  clearCatalogCache();
  return res.json() as Promise<DatasetRefreshResult>;
}

export async function uploadDataset(file: File, overwrite = false): Promise<DatasetUploadResult> {
  const body = new FormData();
  body.append('file', file);
  const url = new URL(ENDPOINTS.gpkg.upload, window.location.origin);
  if (overwrite) url.searchParams.set('overwrite', 'true');

  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: { ...getAuthHeader() },
    body,
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => null);
    const message = typeof detail?.detail === 'string'
      ? detail.detail
      : `Upload failed: ${res.status} ${res.statusText}`;
    throw new Error(message);
  }
  clearCatalogCache();
  return res.json() as Promise<DatasetUploadResult>;
}

/**
 * Resolve the first dataset/layer matching a predicate, scanning the catalog.
 * Useful for bootstrapping defaults (e.g. "find anything that looks like
 * airspaces").
 */
export async function findLayer(
  predicate: (dataset: DatasetSummary, layer: DatasetLayer) => boolean,
): Promise<{ dataset: string; layer: string } | null> {
  const datasets = await listDatasets();
  for (const d of datasets) {
    for (const l of getDatasetLayers(d)) {
      if (predicate(d, l)) return { dataset: d.id, layer: l.name };
    }
  }
  return null;
}

export function clearCatalogCache(): void {
  cache.clear();
}
